import { useState } from "react";
import { COUNTRIES } from "@/lib/countries";
import { useLang } from "@/lib/i18n";
import { ChevronDown } from "lucide-react";

/** Reusable signup fields: first name, last name, email, password, dial-code + phone, country.
 * Onchange returns full form object.
 */
export default function SignupFields({ form, setForm, prefix = "signup", stacked = false }) {
  const { t } = useLang();
  const [openDial, setOpenDial] = useState(false);
  const [openCountry, setOpenCountry] = useState(false);
  const [dialSearch, setDialSearch] = useState("");
  const [countrySearch, setCountrySearch] = useState("");

  const dialCountry =
    COUNTRIES.find((c) => c.dial === form.dial_code && c.code === form.country) ||
    COUNTRIES.find((c) => c.dial === form.dial_code) ||
    COUNTRIES[0];
  const selectedCountry =
    COUNTRIES.find((c) => c.code === form.country) || COUNTRIES[0];

  const filterList = (q) => (c) =>
    !q ||
    c.name.toLowerCase().includes(q.toLowerCase()) ||
    c.dial.includes(q) ||
    c.code.toLowerCase().includes(q.toLowerCase());

  return (
    <div className="space-y-4">
      <div className={stacked ? "space-y-4" : "grid grid-cols-2 gap-3"}>
        <Field
          label={t("field.firstName")}
          value={form.first_name}
          onChange={(v) => setForm({ ...form, first_name: v })}
          testid={`${prefix}-first-name`}
          required
        />
        <Field
          label={t("field.lastName")}
          value={form.last_name}
          onChange={(v) => setForm({ ...form, last_name: v })}
          testid={`${prefix}-last-name`}
          required
        />
      </div>

      <Field
        label={t("field.email")}
        type="email"
        value={form.email}
        onChange={(v) => setForm({ ...form, email: v })}
        testid={`${prefix}-email`}
        required
      />

      <div>
        <label className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
          {t("field.phone")}
        </label>
        <div className="flex gap-2">
          <div className="relative">
            <button
              type="button"
              data-testid={`${prefix}-dial-btn`}
              onClick={() => {
                setOpenDial(!openDial);
                setOpenCountry(false);
              }}
              className="bg-transparent border border-white/20 hover:border-white/40 px-3 py-3 text-sm font-mono flex items-center gap-2 min-w-[110px]"
            >
              <span className="text-lg leading-none">{dialCountry.flag}</span>
              <span>{dialCountry.dial}</span>
              <ChevronDown className="w-3 h-3 opacity-60" />
            </button>
            {openDial && (
              <div className="absolute z-40 mt-1 w-72 max-h-72 overflow-y-auto bg-[#0A0A0A] border border-white/20 shadow-2xl">
                <input
                  autoFocus
                  value={dialSearch}
                  onChange={(e) => setDialSearch(e.target.value)}
                  placeholder="Search country…"
                  className="w-full sticky top-0 bg-black border-b border-white/10 px-3 py-2 text-xs font-mono outline-none"
                />
                {COUNTRIES.filter(filterList(dialSearch)).map((c) => (
                  <button
                    key={c.code + c.dial}
                    type="button"
                    data-testid={`${prefix}-dial-opt-${c.code}`}
                    onClick={() => {
                      setForm({ ...form, dial_code: c.dial });
                      setOpenDial(false);
                      setDialSearch("");
                    }}
                    className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/10 text-sm"
                  >
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-white/50">{c.dial}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            data-testid={`${prefix}-phone`}
            type="tel"
            required
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="555 000 0000"
            className="flex-1 bg-transparent border border-white/20 focus:border-white focus:ring-1 focus:ring-white outline-none px-3 py-3 text-sm font-mono placeholder:text-white/30"
          />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
          {t("field.country")}
        </label>
        <div className="relative">
          <button
            type="button"
            data-testid={`${prefix}-country-btn`}
            onClick={() => {
              setOpenCountry(!openCountry);
              setOpenDial(false);
            }}
            className="w-full bg-transparent border border-white/20 hover:border-white/40 px-3 py-3 text-sm font-mono flex items-center gap-2"
          >
            <span className="text-lg leading-none">{selectedCountry.flag}</span>
            <span className="flex-1 text-left">{selectedCountry.name}</span>
            <ChevronDown className="w-3 h-3 opacity-60" />
          </button>
          {openCountry && (
            <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto bg-[#0A0A0A] border border-white/20 shadow-2xl">
              <input
                autoFocus
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search country…"
                className="w-full sticky top-0 bg-black border-b border-white/10 px-3 py-2 text-xs font-mono outline-none"
              />
              {COUNTRIES.filter(filterList(countrySearch)).map((c) => (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`${prefix}-country-opt-${c.code}`}
                  onClick={() => {
                    setForm({ ...form, country: c.code });
                    setOpenCountry(false);
                    setCountrySearch("");
                  }}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/10 text-sm"
                >
                  <span className="text-lg leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Field
        label={t("field.password")}
        type="password"
        value={form.password}
        onChange={(v) => setForm({ ...form, password: v })}
        placeholder="Min. 4"
        testid={`${prefix}-password`}
        required
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, required }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
        {label}
      </span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border border-white/20 focus:border-white focus:ring-1 focus:ring-white outline-none px-3 py-3 text-sm font-mono placeholder:text-white/30"
      />
    </label>
  );
}

export const emptySignupForm = () => ({
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  phone: "",
  dial_code: "+1",
  country: "US",
});
